import React, { useState, useEffect, useCallback } from 'react';
import styles from './index.module.less';

interface RippleDot {
  key: string;
  angle: number;
  radius: number;
  size: number;
  birthTime: number;
}

interface RippleEffectProps {
  /** 是否激活涟漪效果 */
  isActive: boolean;
  /** 涟漪点数量 */
  dotCount?: number;
  /** 涟漪点生命周期（毫秒） */
  dotLifetime?: number;
  /** 涟漪点生成间隔（毫秒） */
  dotInterval?: number;
  /** 涟漪半径范围 */
  radiusRange?: { min: number; max: number };
  /** 涟漪点大小范围 */
  sizeRange?: { min: number; max: number };
  /** 涟漪颜色 */
  color?: string;
  /** 额外的样式类名 */
  className?: string;
}

/**
 * 涟漪效果组件
 * 负责生成和管理动态涟漪点动画
 */
function RippleEffect({
  isActive = true,
  dotCount = 16,
  dotLifetime = 1800,
  dotInterval = 120,
  radiusRange = { min: 170, max: 200 },
  sizeRange = { min: 12, max: 20 },
  color = 'rgba(46, 199, 167, 1)',
  className,
}: RippleEffectProps) {
  const [rippleDots, setRippleDots] = useState<RippleDot[]>([]);

  // 创建涟漪点
  const createDot = useCallback(
    (key: number): RippleDot => {
      const angle = Math.random() * 2 * Math.PI;
      const radius = radiusRange.min + Math.random() * (radiusRange.max - radiusRange.min);
      const size = sizeRange.min + Math.random() * (sizeRange.max - sizeRange.min);

      return {
        key: `${Date.now()}-${key}-${Math.random()}`,
        angle,
        radius,
        size,
        birthTime: Date.now(),
      };
    },
    [radiusRange, sizeRange]
  );

  // 涟漪点生成和清理逻辑（使用 requestAnimationFrame 优化性能）
  useEffect(() => {
    if (!isActive) {
      setRippleDots([]);
      return;
    }

    let dotKey = 0;
    let lastGenerateTime = Date.now();
    let animationFrameId: number;
    let isCleanedUp = false;

    // 使用 requestAnimationFrame 替代 setInterval，与浏览器重绘同步
    const animate = () => {
      if (isCleanedUp) return;

      const now = Date.now();

      setRippleDots((prev) => {
        // 清理过期的涟漪点
        const activeDots = prev.filter((dot) => now - dot.birthTime < dotLifetime);

        // 根据间隔时间和最大数量限制添加涟漪点
        if (now - lastGenerateTime >= dotInterval && activeDots.length < dotCount) {
          activeDots.push(createDot(dotKey++));
          lastGenerateTime = now;
        }

        return activeDots;
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    // 启动动画循环
    animationFrameId = requestAnimationFrame(animate);

    return () => {
      isCleanedUp = true;
      cancelAnimationFrame(animationFrameId);
    };
  }, [isActive, dotCount, dotLifetime, dotInterval, createDot]);

  // 如果不激活，不渲染任何内容
  if (!isActive) {
    return null;
  }

  return (
    <div className={`${styles.rippleContainer} ${className || ''}`}>
      {rippleDots.map((dot) => {
        // 使用 CSS 动画变量，让浏览器的合成器处理动画，减少主线程压力
        const x = dot.radius * Math.cos(dot.angle);
        const y = dot.radius * Math.sin(dot.angle);
        const animationDuration = `${dotLifetime}ms`;

        return (
          <div
            key={dot.key}
            className={styles.rippleDot}
            style={{
              left: `calc(50% + ${x}px)`,
              top: `calc(50% + ${y}px)`,
              background: color,
              width: dot.size,
              height: dot.size,
              // 使用 CSS 动画替代 JS 计算，性能更好
              animation: `rippleFadeScale ${animationDuration} ease-out forwards`,
              // @ts-ignore - CSS custom properties
              '--dot-lifetime': animationDuration,
            }}
          />
        );
      })}
    </div>
  );
}

export default RippleEffect;
export type { RippleEffectProps, RippleDot };
